import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useMemo } from 'react';
import { adminService } from '@/lib/api/services/admin.service';
import { MessageBubble } from '@/components/chat/MessageBubble';
import { DateSeparator } from '@/components/chat/DateSeparator';
import { Loader2, User, ArrowLeft, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { Message } from '@/lib/api/services/chat.service';

interface AdminChatViewProps {
  conversationId: string;
  onBack: () => void;
}

interface ConversationParticipant {
  id: string;
  conversation_id: string;
  user_id: string;
  role: 'genius' | 'client';
  display_name?: string | null;
  profile_picture_url?: string | null;
  profile_id?: string | null;
}

export function AdminChatView({ conversationId, onBack }: AdminChatViewProps) {
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const { data: conversationDetails, isLoading: loadingDetails } = useQuery({
    queryKey: ['admin', 'conversation', conversationId],
    queryFn: () => adminService.getConversationDetails(conversationId),
  });

  // Helper to normalize conversation_analysis (can be object or array)
  const getAnalysis = (analysis: unknown) => {
    if (!analysis) return null;
    if (Array.isArray(analysis)) return analysis[0] || null;
    return analysis;
  };

  // Get messages from admin API response
  const messages = useMemo(() => {
    return (conversationDetails?.messages || []) as Message[];
  }, [conversationDetails?.messages]);

  // Get participants
  const participants = useMemo(() => {
    return (conversationDetails?.conversation?.conversation_participants || []) as ConversationParticipant[];
  }, [conversationDetails?.conversation?.conversation_participants]);

  const analysis = useMemo(() => {
    return getAnalysis(conversationDetails?.conversation?.conversation_analysis);
  }, [conversationDetails?.conversation?.conversation_analysis]);

  // Get tags from conversation details (from conversation_tag_assignments)
  const tags = useMemo(() => {
    return (conversationDetails?.tags || []) as string[];
  }, [conversationDetails?.tags]);

  const geniusParticipant = useMemo(() => {
    return participants.find((p: ConversationParticipant) => p.role === 'genius');
  }, [participants]);

  const clientParticipant = useMemo(() => {
    return participants.find((p: ConversationParticipant) => p.role === 'client');
  }, [participants]);
  
  // Create a map of user IDs to display names from conversation participants
  const participantDisplayNames = useMemo(() => {
    const map = new Map<string, string | null>();
    participants.forEach((participant) => {
      map.set(participant.user_id, participant.display_name || null);
    });
    return map;
  }, [participants]);

  // Group messages by date and insert date separators
  const messagesWithSeparators = useMemo(() => {
    if (!messages.length) return [];
    
    const result: Array<{ type: 'message'; message: Message; index: number } | { type: 'separator'; date: Date }> = [];
    let lastDate: Date | null = null;
    
    messages.forEach((message, index) => {
      const messageDate = new Date(message.created_at);
      messageDate.setHours(0, 0, 0, 0);
      
      // Check if we need a date separator
      if (!lastDate || messageDate.getTime() !== lastDate.getTime()) {
        result.push({ type: 'separator', date: new Date(message.created_at) });
        lastDate = messageDate;
      }
      
      result.push({ type: 'message', message, index });
    });
    
    return result;
  }, [messages]);

  // Auto-scroll to bottom when messages load
  useEffect(() => {
    if (messagesContainerRef.current && messages.length > 0) {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [messages.length]);

  const getSentimentColor = (sentiment: string | null | undefined) => {
    if (sentiment === 'positive') return 'bg-emerald-100 text-emerald-700';
    if (sentiment === 'negative') return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-slate-700';
  };

  const handleParticipantClick = (participant: ConversationParticipant) => {
    if (participant.profile_id) {
      const url = participant.role === 'genius' 
        ? `/genius/${participant.profile_id}`
        : `/client/${participant.profile_id}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (loadingDetails) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!conversationDetails) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-slate-500">Conversation not found</p>
        <Button onClick={onBack} variant="outline" className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Conversations
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Header */}
        <div className="flex-shrink-0 p-4 border-b border-slate-200 flex items-center justify-between bg-white">
          <div className="flex items-center gap-4 flex-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            
            {/* Both Participants */}
            <div className="flex items-center gap-4 flex-1">
              {geniusParticipant && (
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={geniusParticipant.profile_picture_url || undefined} />
                    <AvatarFallback className="font-semibold bg-[#4669ed] text-white">
                      {geniusParticipant.display_name
                        ?.split(' ')
                        .map((n: string) => n[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2) || 'G'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-sm text-slate-900">
                      {geniusParticipant.display_name || 'Freelancer'}
                    </h3>
                    <p className="text-xs text-slate-500">Freelancer</p>
                  </div>
                </div>
              )}
              
              {/* Conversation Arrow/VS */}
              {(geniusParticipant && clientParticipant) && (
                <div className="flex items-center gap-2 text-slate-400">
                  <div className="h-px w-8 bg-slate-300"></div>
                </div>
              )}
              
              {clientParticipant && (
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={clientParticipant.profile_picture_url || undefined} />
                    <AvatarFallback className="font-semibold bg-slate-500 text-white">
                      {clientParticipant.display_name
                        ?.split(' ')
                        .map((n: string) => n[0])
                        .join('')
                        .toUpperCase()
                        .slice(0, 2) || 'C'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-sm text-slate-900">
                      {clientParticipant.display_name || 'Client'}
                    </h3>
                    <p className="text-xs text-slate-500">Client</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Messages Area (Read-only) */}
        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 min-h-0 bg-white">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-slate-500">No messages in this conversation</p>
            </div>
          ) : (
            <div className="space-y-1">
              {messagesWithSeparators.map((item) => {
                if (item.type === 'separator') {
                  return <DateSeparator key={`separator-${item.date.getTime()}`} date={item.date} />;
                } else {
                  const senderName = participantDisplayNames.get(item.message.sender_id) || undefined;
                  // For admin view, we don't have a "current user" concept, so we'll show all messages
                  // We can determine if it's from genius or client based on participant role
                  const senderParticipant = participants.find(p => p.user_id === item.message.sender_id);
                  const isGeniusMessage = senderParticipant?.role === 'genius';
                  
                  return (
                    <MessageBubble
                      key={item.message.id}
                      message={item.message}
                      isOwn={isGeniusMessage} // Show genius messages on right, client on left
                      senderName={senderName}
                      isLastMessage={item.index === messages.length - 1}
                    />
                  );
                }
              })}
            </div>
          )}
        </div>
      </div>

      {/* Analytics/Metadata Sidebar */}
      <div className="w-[400px] bg-slate-50 border-l border-slate-200 overflow-y-auto flex flex-col">
        <div className="p-6 space-y-6">
          {/* Conversation Info */}
          <div className="bg-white rounded-[16px] border border-slate-200 p-6">
            <h3 className="text-base font-bold text-slate-900 mb-4">Conversation Information</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-slate-600 mb-1">Conversation ID</p>
                <p className="text-sm font-mono font-semibold text-slate-900 break-all">
                  {conversationDetails.conversation?.id}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-600 mb-1">Created</p>
                <p className="text-sm font-semibold text-slate-900">
                  {new Date(conversationDetails.conversation?.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-600 mb-1">Last Message</p>
                <p className="text-sm font-semibold text-slate-900">
                  {conversationDetails.conversation?.last_message_at
                    ? new Date(conversationDetails.conversation.last_message_at).toLocaleString()
                    : 'N/A'}
                </p>
              </div>
              {conversationDetails.opportunity && (
                <div>
                  <p className="text-xs text-slate-600 mb-1">Linked Opportunity</p>
                  <p className="text-sm font-semibold text-slate-900">
                    {conversationDetails.opportunity.title}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Participants */}
          <div className="bg-white rounded-[16px] border border-slate-200 p-6">
            <h3 className="text-base font-bold text-slate-900 mb-4">Participants</h3>
            <div className="space-y-3">
              {geniusParticipant && (
                <div
                  onClick={() => handleParticipantClick(geniusParticipant)}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer"
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={geniusParticipant.profile_picture_url || undefined} />
                    <AvatarFallback className="bg-[#4669ed] text-white">
                      {geniusParticipant.display_name?.charAt(0).toUpperCase() || 'G'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {geniusParticipant.display_name || 'Freelancer'}
                    </p>
                    <p className="text-xs text-slate-500">Freelancer (Genius)</p>
                  </div>
                  <User className="w-4 h-4 text-slate-400" />
                </div>
              )}
              {clientParticipant && (
                <div
                  onClick={() => handleParticipantClick(clientParticipant)}
                  className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer"
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={clientParticipant.profile_picture_url || undefined} />
                    <AvatarFallback className="bg-slate-500 text-white">
                      {clientParticipant.display_name?.charAt(0).toUpperCase() || 'C'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">
                      {clientParticipant.display_name || 'Client'}
                    </p>
                    <p className="text-xs text-slate-500">Client</p>
                  </div>
                  <User className="w-4 h-4 text-slate-400" />
                </div>
              )}
            </div>
          </div>

          {/* Analysis Data */}
          {analysis && (
            <div className="bg-white rounded-[16px] border border-slate-200 p-6">
              <h3 className="text-base font-bold text-slate-900 mb-4">Analysis Data</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-slate-600 mb-2">Overall Sentiment</p>
                  <span className={cn(
                    "inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold",
                    getSentimentColor(analysis.overall_sentiment)
                  )}>
                    {analysis.overall_sentiment?.toUpperCase() || 'N/A'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Sentiment Score</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {analysis.sentiment_score?.toFixed(2) || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Average Sentiment</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {analysis.average_sentiment?.toFixed(2) || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Reply Rate (24h)</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {analysis.reply_rate_24h?.toFixed(1) || 'N/A'}%
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Avg Response Time</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {analysis.average_response_time_hours?.toFixed(2) || 'N/A'}h
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Total Exchanges</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {analysis.total_exchanges || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-600 mb-1">Total Messages</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {conversationDetails.messages?.length || 0}
                    </p>
                  </div>
                </div>
                {analysis.is_flagged && (
                  <div className="pt-4 border-t border-slate-200 space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 rounded-lg bg-red-50">
                        <Flag className="w-4 h-4 text-red-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-red-600 mb-1">Flagged Conversation</p>
                        <p className="text-xs text-slate-500">This conversation has been flagged for review</p>
                      </div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-2">
                      <div>
                        <p className="text-xs font-semibold text-slate-600 mb-1">Flag Reason</p>
                        <p className="text-sm font-semibold text-red-700 capitalize">
                          {analysis.flag_reason ? analysis.flag_reason.replace('_', ' ') : 'Not specified'}
                        </p>
                      </div>
                      {analysis.flag_details && (
                        <div>
                          <p className="text-xs font-semibold text-slate-600 mb-1">Flag Details</p>
                          <div className="bg-white border border-red-100 rounded p-2 max-h-40 overflow-auto">
                            <pre className="text-xs text-slate-700 whitespace-pre-wrap">
                              {JSON.stringify(analysis.flag_details, null, 2)}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {tags && tags.length > 0 && (
                  <div className="pt-4 border-t border-slate-200">
                    <p className="text-xs font-semibold text-slate-600 mb-2">Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag: string, idx: number) => (
                        <Badge
                          key={idx}
                          variant="outline"
                          className="px-2.5 py-1 text-xs font-semibold bg-blue-50 text-blue-700 border-blue-200"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

